// The key a localized string gets when its text is typed straight into a field (spec/dashboard.md section 6.4,
// "localized string"): `<OWNER>_<SLUG>_<PARAM>`, upper case, made unique in its group with `_2`, `_3`…

/** The longest slug of the typed text that goes into a derived key. */
export const SLUG_MAX = 40;

/** Upper case, every run of anything but A–Z / 0–9 as one `_`, no `_` at either end; `max` caps the length. */
export function slugKey(text: string, max = Number.POSITIVE_INFINITY): string {
  const slug = text
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return slug.length > max ? slug.slice(0, max).replace(/_+$/, '') : slug;
}

/** The short name of a template (`SmartObjects.GameOffer` → `GameOffer`). */
export function templateShortName(template: string | null | undefined): string {
  if (!template) return '';
  return template.split('.').pop() ?? template;
}

/**
 * `GAMEOFFER_SUMMER_DEAL_NAME` for the text "Summer Deal" in the `name` field of a `SmartObjects.GameOffer`: the owner
 * template's short name (`DOC` when unknown), the text slugged to at most 40 characters, the parameter name; a key
 * already taken in the group gets `_2`, `_3`… An empty segment (a text with no letter or digit) is left out.
 */
export function deriveStringKey(ownerTemplate: string | null | undefined, text: string, paramName: string, taken: ReadonlySet<string> | ((key: string) => boolean) = new Set()): string {
  const isTaken = typeof taken === 'function' ? taken : (k: string) => taken.has(k);
  const owner = slugKey(templateShortName(ownerTemplate)) || 'DOC';
  const base = [owner, slugKey(text, SLUG_MAX), slugKey(paramName)].filter((s) => s.length > 0).join('_');
  if (!isTaken(base)) return base;
  for (let n = 2; ; n++) {
    const candidate = `${base}_${n}`;
    if (!isTaken(candidate)) return candidate;
  }
}
